<template>
	<v-card class="sidebar">
		<v-list class="sidebar-list">
			<v-list-item
				v-for="link in prettyAppBaseMenu"
				:key="link.id"
				class="sidebar-list__item"
				:ripple="false"
			>
				<v-list-item-content class="sidebar-list__content">
					<a
						v-if="link.route.name === 'web-view'"
						@click.prevent="toggleLink(link.params.url)"
						class="text-decoration-none"
					>
						<v-list-item-title class="sidebar-list__title">
							<Icon
								v-if="link.icon"
								class="sidebar-list__title icon-class"
								:size="24"
								:type="link.icon"
								:color="
									isActiveLink(link)
										? 'var(--accent-color)'
										: 'var(--accent-color)'
								"
							/>
							<span class="ml-2 sidebar-list__text"> {{ link.title }} </span>
						</v-list-item-title>
					</a>
					<router-link
						v-else
						active-class="active"
						:to="link.route"
						exact
						class="text-decoration-none"
						@click.native="handleMenuClick(link)"
					>
						<v-list-item-title class="sidebar-list__title">
							<Icon
								v-if="link.icon"
								class="sidebar-list__title icon-class"
								:size="24"
								:type="link.icon"
								:color="
									isActiveLink(link)
										? 'var(--accent-color)'
										: 'var(--accent-color)'
								"
							/>
							<span class="ml-2 sidebar-list__text"> {{ link.title }} </span>
						</v-list-item-title>
					</router-link>
				</v-list-item-content>
			</v-list-item>
			<div v-if="shouldLeftBannerShow" class="mt-3">
				<AdBanner />
			</div>
		</v-list>
	</v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import plusIcon from '@/assets/images/icon-plus.svg'
import Dwh from '@/mixins/Dwh'
import AdBanner from '@/components/Layouts/parts/AdBanner.vue'

export default {
	name: 'AppSidebar',
	components: { AdBanner },
	mixins: [Dwh],
	watch: {
		selectedTags: {
			handler(val) {
				this.$eventBus.$emit('selected-tags', val)
			},
		},
	},
	props: {
		menuListHandler: {
			type: Function,
			default: () => ({}),
		},
	},
	data() {
		return {
			plusIcon: plusIcon,
			triggerMenuNames: [
				{ name: 'threads', action: 'menu_news_feed' },
				{ name: 'events', action: 'menu_events' },
				{ name: 'resources', action: 'menu_resources' },
				{ name: 'events', action: 'menu_events' },
				{ name: 'users', action: 'menu_users' },
				{ name: 'forums', action: 'menu_forums' },
				{ name: 'rss-feed', action: 'menu_rss_feed' },
				{ name: 'directory', action: 'menu_directory' },
			],
		}
	},
	computed: {
		...mapGetters('user', ['popularAuthors', 'isPopularAuthorsLoading']),
		...mapGetters('settings', ['appCore']),
		...mapGetters('auth', ['profile', 'mapRegisterToken']),
		...mapGetters('settings', ['web']),
		userChapters() {
			return this.profile.chapters
		},
		userIsMember() {
			return this.profile.is_member
		},
		prettyAppBaseMenu() {
			if (!this.appCore) {
				return []
			}
			return this.appCore.appMenuNormalized.baseMenuItems
				.filter((item) => {
					if (!item.title || !item.route) {
						return false
					}
					if (item.chapters && item.chapters.length) {
						let result = false
						item.chapters.forEach((chapter) => {
							if (
								this.userChapters.some(
									(userChapter) => userChapter.chapter_id === chapter
								)
							) {
								result = true
							}
						})
						if (!result) {
							return false
						}
					}
					if (item.role === null || (item.block && item.block === 2)) {
						return true
					} else if (
						(item.role && this.userIsMember) ||
						(!item.role && !this.userIsMember)
					) {
						return true
					}
					return false
				})
				.map((item) => {
					return item
				})
		},
		shouldLeftBannerShow() {
			if (!this.web) return false
			const width = this.$vuetify.breakpoint.width
			return width >= 1024 && width < 1399
		},
	},
	methods: {
		...mapActions('user', ['fetchPopularAuthors']),
		...mapActions('auth', ['getMapRegisterToken']),
		isMapUrlValid(url) {
			try {
				const arr = JSON.parse(this.appCore.parsing_url)
				const link = new URL(url)
				return arr.some((allowed) => link.origin === allowed)
			} catch (e) {
				console.error(e)
				return false
			}
		},
		async toggleLink(link) {
			const target = link

			if (!target) return

			if (this.isMapUrlValid(target)) {
				try {
					await this.getMapRegisterToken()
					if (!this.mapRegisterToken) {
						console.error('Token not received')
						return
					}
					const urlWithToken = `${target}${target.includes('?') ? '&' : '?'}code=${
						this.mapRegisterToken
					}`
					window.open(urlWithToken, '_self')
				} catch (e) {
					console.error(e)
				}
			} else {
				window.open(target, '_self')
			}
		},
		isActiveLink(link) {
			if (link.route.name === 'web-view') return false
			const targetRoute = this.$router.resolve(link.route).route
			return targetRoute.fullPath === this.$route.fullPath
		},

		handleMenuClick(link) {
			const trigger = this.triggerMenuNames.find(
				(trigger) => trigger.name === link.route.name
			)
			if (trigger) {
				this.sendTrigger({
					category: 'Navigation',
					action: trigger.action,
					module: 'Menu',
					title: link.title,
					content_chapter_id: link.chapters,
				})
			} else if (link.route.startsWith('http')) {
				this.sendTrigger({
					category: 'Navigation',
					action: 'menu_web_view',
					module: 'Menu',
					title: link.title,
					content_chapter_id: link.chapters,
				})
			}
		},
	},
}
</script>

<style lang="scss">
.active {
	text-decoration: none;

	> div {
		font-family: Inter, sans-serif;
		font-weight: 700;
		font-size: 15px;
		line-height: 20px !important;
		letter-spacing: 0;
	}
}

.sidebar {
	width: 294px;
	background-color: unset !important;
	border-radius: 10px !important;
	box-shadow: none !important;
}

.sidebar-list {
	position: sticky;
	top: 63px;
	padding: 20px 0 0 0 !important;
	background-color: unset !important;
	gap: 8px;
	&__text {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: inline-block;
		max-width: 219px;
		vertical-align: middle;
	}

	&__item {
		font-size: 10px !important;
		font-family: 'Inter', sans-serif;
		font-weight: 500;
		line-height: 20px;
		padding: 0;
		min-height: 38px;
		display: flex;
		align-items: center;
	}

	&__content {
		padding: 0 0 20px 0;
		display: flex;
		align-items: center;
	}

	&__title {
		max-width: 275px;
		width: 100%;
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 15px;
		line-height: 20px;
		letter-spacing: 0;
		color: var(--accent-color) !important;
		display: flex;
		align-items: center;
	}

	.icon-class {
		width: 24px !important;
		height: 24px !important;
	}
}

.create-post {
	&__submit {
		width: 250px;
		height: 54px;
		margin-top: 30px;
		background: rgb(65, 161, 216) !important;
		border-radius: 10px;
		text-transform: capitalize !important;
		color: rgb(255, 255, 255) !important;
		font-size: 16px !important;
		font-family: 'Inter', sans-serif !important;
		font-weight: bold;
		align-items: center;
	}
}
</style>
